import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode
} from 'react';
import { translations } from '../translations';

// Types
export type Language = 'fr' | 'nl' | 'en';

export interface LanguageContextType {
  t: (key: string, ...args: any[]) => string;
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
}

// Créer le contexte
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Hook personnalisé pour utiliser le contexte
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Provider du contexte
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Récupérer la langue du localStorage ou utiliser le français par défaut
  const getInitialLanguage = (): Language => {
    const savedLanguage = localStorage.getItem('language') as Language | null;
    return savedLanguage || 'fr';
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Mettre à jour la langue et sauvegarder dans localStorage
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('language', newLanguage);
    // Mettre à jour l'attribut lang du document HTML pour l'accessibilité
    document.documentElement.lang = newLanguage;
  };

  // Fonction de traduction
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }

    if (typeof value === 'string') {
      return value;
    }

    console.warn(`Translation for key '${key}' is not a string.`);
    return key;
  };

  // Initialiser l'attribut lang du document HTML
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    currentLanguage: language,
    setLanguage,
    t
  };

  return (
      <LanguageContext.Provider value={value}>
        {children}
      </LanguageContext.Provider>
  );
};

export default LanguageContext;
